import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Pencil, Trash2, Plus, Upload, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { MultiLanguageInput } from "@/components/admin/MultiLanguageInput";
import { MultiLanguageTextarea } from "@/components/admin/MultiLanguageTextarea";

const AdminBlog = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editingPost, setEditingPost] = useState<any>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<'es' | 'en' | 'fr'>('es');

  const { data: posts, isLoading } = useQuery({
    queryKey: ["admin-blog-posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("blog_posts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-blog-posts"] });
      toast({ title: "Post eliminado correctamente" });
    },
  });

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return null;

    setIsUploading(true);
    try {
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('blog-images')
        .upload(filePath, imageFile);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({ 
        title: "Error al subir imagen", 
        description: "No se pudo subir la imagen. Intenta de nuevo.",
        variant: "destructive" 
      });
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const saveMutation = useMutation({
    mutationFn: async (post: any) => {
      let imageUrl = post.featured_image;

      if (imageFile) {
        const uploadedUrl = await uploadImage();
        if (uploadedUrl) {
          imageUrl = uploadedUrl;
        }
      }

      const postData = { ...post, featured_image: imageUrl };

      if (post.id) {
        const { error } = await supabase
          .from("blog_posts")
          .update(postData)
          .eq("id", post.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("blog_posts").insert([postData]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-blog-posts"] });
      toast({ title: "Post guardado correctamente" });
      setIsEditing(false);
      setEditingPost(null);
      setImageFile(null);
      setImagePreview("");
    },
  });

  const handleEdit = (post: any) => {
    setEditingPost(post);
    setImagePreview(post.featured_image || "");
    setImageFile(null);
    setIsEditing(true);
  };

  const handleNew = () => {
    setEditingPost({
      title: "",
      title_en: "",
      title_fr: "",
      slug: "",
      excerpt: "",
      excerpt_en: "",
      excerpt_fr: "",
      content: "",
      content_en: "",
      content_fr: "",
      author: "",
      featured_image: "",
      category: "",
      is_published: false,
    });
    setImagePreview("");
    setImageFile(null);
    setSelectedLanguage('es');
    setIsEditing(true);
  };

  if (isEditing && editingPost) {
    const getTitleValue = () => {
      if (selectedLanguage === 'es') return editingPost.title || "";
      if (selectedLanguage === 'en') return editingPost.title_en || "";
      return editingPost.title_fr || "";
    };

    const getExcerptValue = () => {
      if (selectedLanguage === 'es') return editingPost.excerpt || "";
      if (selectedLanguage === 'en') return editingPost.excerpt_en || "";
      return editingPost.excerpt_fr || "";
    };

    const getContentValue = () => {
      if (selectedLanguage === 'es') return editingPost.content || "";
      if (selectedLanguage === 'en') return editingPost.content_en || "";
      return editingPost.content_fr || "";
    };

    const handleTitleChange = (value: string) => {
      if (selectedLanguage === 'es') setEditingPost({ ...editingPost, title: value });
      else if (selectedLanguage === 'en') setEditingPost({ ...editingPost, title_en: value });
      else setEditingPost({ ...editingPost, title_fr: value });
    };

    const handleExcerptChange = (value: string) => {
      if (selectedLanguage === 'es') setEditingPost({ ...editingPost, excerpt: value });
      else if (selectedLanguage === 'en') setEditingPost({ ...editingPost, excerpt_en: value });
      else setEditingPost({ ...editingPost, excerpt_fr: value });
    };

    const handleContentChange = (value: string) => {
      if (selectedLanguage === 'es') setEditingPost({ ...editingPost, content: value });
      else if (selectedLanguage === 'en') setEditingPost({ ...editingPost, content_en: value });
      else setEditingPost({ ...editingPost, content_fr: value });
    };

    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{editingPost.id ? "Editar Post" : "Nuevo Post"}</CardTitle>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={selectedLanguage === 'es' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedLanguage('es')}
                >
                  🇪🇸 ES
                </Button>
                <Button
                  type="button"
                  variant={selectedLanguage === 'en' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedLanguage('en')}
                >
                  🇬🇧 EN
                </Button>
                <Button
                  type="button"
                  variant={selectedLanguage === 'fr' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedLanguage('fr')}
                >
                  🇫🇷 FR
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Título ({selectedLanguage.toUpperCase()})</Label>
              <Input
                value={getTitleValue()}
                onChange={(e) => handleTitleChange(e.target.value)}
              />
            </div>
            <div>
              <Label>Slug</Label>
              <Input
                value={editingPost.slug}
                onChange={(e) => setEditingPost({ ...editingPost, slug: e.target.value })}
              />
            </div>
            <div>
              <Label>Autor</Label>
              <Input
                value={editingPost.author}
                onChange={(e) => setEditingPost({ ...editingPost, author: e.target.value })}
              />
            </div>
            <div>
              <Label>Categoría</Label>
              <Input
                value={editingPost.category}
                onChange={(e) => setEditingPost({ ...editingPost, category: e.target.value })}
              />
            </div>
            <div>
              <Label>Imagen destacada</Label>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleImageChange}
                    className="flex-1"
                  />
                  {imagePreview && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview("");
                        setEditingPost({ ...editingPost, featured_image: "" });
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {imagePreview && (
                  <div className="relative w-full h-48 rounded-lg overflow-hidden border">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Formatos permitidos: JPG, PNG, WEBP. Tamaño máximo: 5MB
                </p>
              </div>
            </div>
            <div>
              <Label>Extracto ({selectedLanguage.toUpperCase()})</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={getExcerptValue()}
                onChange={(e) => handleExcerptChange(e.target.value)}
                rows={3}
              />
            </div>
            <div>
              <Label>Contenido ({selectedLanguage.toUpperCase()})</Label>
              <textarea
                className="flex min-h-[200px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={getContentValue()}
                onChange={(e) => handleContentChange(e.target.value)}
                rows={10}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editingPost.is_published}
                onChange={(e) => setEditingPost({ ...editingPost, is_published: e.target.checked })}
              />
              <Label>Publicado</Label>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => saveMutation.mutate(editingPost)}
                disabled={isUploading || saveMutation.isPending}
              >
                {isUploading ? (
                  <>
                    <Upload className="mr-2 h-4 w-4 animate-spin" />
                    Subiendo imagen...
                  </>
                ) : saveMutation.isPending ? (
                  "Guardando..."
                ) : (
                  "Guardar"
                )}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setIsEditing(false);
                  setImageFile(null);
                  setImagePreview("");
                }}
                disabled={isUploading || saveMutation.isPending}
              >
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Gestión de Blog</h1>
        <Button onClick={handleNew}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo Post
        </Button>
      </div>

      {isLoading ? (
        <div>Cargando...</div>
      ) : (
        <div className="grid gap-4">
          {posts?.map((post) => (
            <Card key={post.id}>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-semibold">{post.title}</h3>
                      {post.is_published ? (
                        <Badge>Publicado</Badge>
                      ) : (
                        <Badge variant="secondary">Borrador</Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground mb-2">{post.excerpt}</p>
                    <div className="text-sm text-muted-foreground">
                      Por {post.author} • {post.category}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={() => handleEdit(post)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => deleteMutation.mutate(post.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
